import type { TankGedragOverzicht, TankGedragRegel, TankGedragSoort } from '@fleet/shared';
import { euro } from '@fleet/shared';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { InfoTip } from '../../components/ui/InfoTip';
import { MeerTonen } from '../../components/ui/MeerTonen';
import { Panel } from '../../components/ui/Panel';
import { PersoonLink } from '../../components/ui/EntiteitLink';

/** Rows on screen while collapsed. */
const CAP = 6;

const DUUR = '#B0543B';
const GOEDKOOP = '#2A7050';
const LIJN = '#F0E8DC';

/**
 * Least the bar scale ever spans, as a deviation either way.
 *
 * Scaled purely on the widest deviation present, a month in which everybody
 * lands within 2% of each other would draw the same dramatic staircase as a year
 * in which they are 30% apart. The floor keeps a tight field looking tight: over
 * June the bars are stubs, and that is the honest picture.
 */
const MIN_SCHAAL = 0.1;

/** A unit price wants its third decimal — the difference in a kWh lives there. */
function prijs(n: number): string {
  return `€ ${n.toLocaleString('nl-NL', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
}

function getal(n: number): string {
  return n.toLocaleString('nl-NL', { maximumFractionDigits: 0 });
}

/** `+€ 90,00` / `−€ 84,00` — the sign in front of the symbol, not behind it. */
function verschilTekst(n: number): string {
  return `${n >= 0 ? '+' : '−'}${euro(Math.abs(n))}`;
}

function pct(deel: number): string {
  return `${Math.round(deel * 100)}%`;
}

/**
 * What one person paid, against what the fleet paid in the same months.
 *
 * The bar runs out from the fleet line rather than up from zero, because the
 * quantity it draws is a deviation with a real zero in the middle. Every other
 * bar on this dashboard is a magnitude and starts at the left edge; this one
 * would be unreadable that way — fifteen bars of near-identical length, with the
 * whole story in their last few pixels.
 */
function Rij({
  regel,
  schaal,
  eenheid,
}: {
  regel: TankGedragRegel;
  schaal: number;
  eenheid: string;
}) {
  const afwijking = regel.index - 1;
  const deel = Math.min(1, Math.abs(afwijking) / schaal) * 50;
  const kleur = afwijking > 0 ? DUUR : GOEDKOOP;
  const zwak = !regel.beoordeelbaar;

  // Het percentage en het euroverschil staan hier en niet in de rij. De balk
  // tekent de afwijking al, dus het cijfer erbij was een tweede keer hetzelfde
  // zeggen — en het kostte een tweede tekstregel, waardoor elke rij hoger werd
  // dan zijn eigen balk. Wie het bedrag nodig heeft, is het gesprek aan het
  // voorbereiden en heeft een seconde om te wijzen.
  const toelichting = [
    `${regel.transacties} keer · ${getal(regel.hoeveelheid)} ${eenheid}`,
    zwak
      ? ''
      : `${Math.abs(Math.round(afwijking * 100))}% ${afwijking >= 0 ? 'boven' : 'onder'} de vlootprijs, ${verschilTekst(regel.verschil)}`,
    regel.aandeelDuur != null
      ? `${pct(regel.aandeelDuur)} in het duurste kwart van de vloot, ${pct(regel.aandeelGoedkoop ?? 0)} in het goedkoopste`
      : '',
    zwak ? 'Te weinig om een oordeel op te baseren.' : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      title={toelichting}
      style={{
        display: 'grid',
        // Letterlijk het raster van het wasbudget eronder. Twee lijsten van
        // personen met een balk, recht onder elkaar op dezelfde pagina: als die
        // balken op een andere breedte beginnen en eindigen leest het als twee
        // verschillende soorten grafiek, terwijl het dezelfde vraag is.
        gridTemplateColumns: '132px minmax(0, 1fr) 128px',
        gap: 14,
        alignItems: 'center',
        opacity: zwak ? 0.5 : 1,
      }}
    >
      <PersoonLink
        persoonId={regel.persoonId}
        naam={regel.naam}
        style={{
          fontSize: 13,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      />

      <div
        style={{
          position: 'relative',
          height: 20,
          background: LIJN,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {/* Geen balk onder de drempel. Die tekent het oordeel, en juist bij deze
            rijen slaat hij het hardst uit: één dure tankbeurt van tien liter zet
            een index van 1,09 neer waar de mensen met tweehonderd liter binnen de
            2% blijven. Dimmen was niet genoeg — de langste balk van de kaart hoorde
            bij de rij waarvan we net zeiden dat we er niets van weten. */}
        {!zwak && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: afwijking > 0 ? '50%' : `${50 - deel}%`,
              width: `${deel}%`,
              background: kleur,
              // Vanaf de vlootlijn naar buiten, niet vanuit het midden van de balk
              // zelf: de streep is waar de beweging vandaan komt.
              transformOrigin: afwijking > 0 ? 'left' : 'right',
              animation: 'vsGrowW .6s cubic-bezier(.4,0,.2,1) both',
            }}
          />
        )}
        <span
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            bottom: 0,
            width: 1,
            background: '#111111',
            opacity: 0.55,
          }}
        />
      </div>

      {/* Eén regel, dus even hoog als de balk ernaast. Alleen de prijs: dat is de
          vraag waarmee je hier komt ("wie tankt het duurst"), en het is hetzelfde
          soort getal als het wasbudget eronder op dezelfde plek zet. */}
      <span style={{ fontSize: 12.5, textAlign: 'right', whiteSpace: 'nowrap' }}>
        <strong style={{ color: '#111111' }}>{prijs(regel.gemiddeldePrijs)}</strong>
        {zwak && <span style={{ marginLeft: 7, fontSize: 11, color: '#999999' }}>te weinig</span>}
      </span>
    </div>
  );
}

function Kaart({ soort, periodLabel }: { soort: TankGedragSoort; periodLabel: string }) {
  // Eerst wie te beoordelen valt, op index; de rest eronder en gedimd. Ze staan
  // er wel, want "Bo tankte één keer" is een antwoord op "waar is Bo?", maar ze
  // horen niet tussen de mensen over wie je iets kunt zeggen.
  const beoordeeld = soort.regels.filter((r) => r.beoordeelbaar);
  const rest = soort.regels.filter((r) => !r.beoordeelbaar);
  const rijen = [...beoordeeld, ...rest];
  // Over álle beoordeelde rijen, ook de ingeklapte: anders verspringt de hele
  // kaart zodra je uitklapt en is de balk van vóór het uitklappen een andere
  // maat dan die erna.
  const schaal = Math.max(MIN_SCHAAL, ...beoordeeld.map((r) => Math.abs(r.index - 1)));

  // De vlootcijfers zitten in de toelichting en staan niet als grijze regel onder
  // het bedrag: de panelen hierboven doen dat ook niet, en drie getallen die
  // niemand aanwijst zijn precies waar de `i` voor is.
  const ongekoppeld =
    soort.ongekoppeldeHoeveelheid > 0
      ? ` ${getal(soort.ongekoppeldeHoeveelheid)} ${soort.eenheid} hangt aan geen persoon en telt alleen mee in de vlootprijs.`
      : '';
  const vlootTotaal = `Over deze periode ${getal(soort.transacties)} keer, ${getal(soort.hoeveelheid)} ${soort.eenheid} voor ${euro(soort.betaald)}.`;

  return (
    <Panel>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Eyebrow>
          {soort.label} — {periodLabel}
        </Eyebrow>
        <InfoTip
          uitlijning="links"
          tekst={`Elke transactie wordt afgezet tegen wat de vloot in diezelfde maand per ${soort.eenheid} betaalde. Dat moet, want de prijs zelf beweegt harder dan het verschil tussen mensen — zonder die correctie meet je vooral wie wanneer reed. De streep in het midden is de vlootprijs. ${vlootTotaal}${ongekoppeld}`}
        />
      </div>
      <div
        style={{
          fontSize: 26,
          fontWeight: 900,
          color: '#2A6085',
          marginTop: 4,
          marginBottom: 16,
        }}
      >
        {prijs(soort.vlootPrijs)}
        <span style={{ fontSize: 13, fontWeight: 400, color: '#888888' }}>
          {' '}
          per {soort.eenheid}
        </span>
      </div>

      {rijen.length === 0 ? (
        <div style={{ fontSize: 13, color: '#888888' }}>
          Niemand met een pas heeft in deze periode {soort.label.toLowerCase()} geboekt.
        </div>
      ) : (
        // De knop noemt wie eronder zit en niet alleen hoeveel: ingeklapt zie je
        // de duurste vijf, dus de mensen die het goedkoopst kopen — precies wie je
        // wilt belonen — staan er allemaal achter.
        <MeerTonen
          items={rijen}
          cap={CAP}
          meer={(n) => `+${n} goedkoper tonen`}
          minder="Alleen de duurste tonen"
        >
          {(zicht) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {zicht.map((r) => (
                <Rij key={r.persoonId} regel={r} schaal={schaal} eenheid={soort.eenheid} />
              ))}
            </div>
          )}
        </MeerTonen>
      )}
    </Panel>
  );
}

/**
 * Who buys their energy cheaply and who does not.
 *
 * Two cards rather than one ranking, because petrol and charging cannot be put
 * in one list. Merged, all five worst places go to EV drivers and the petrol
 * drivers sit in the middle — not because they choose worse, but because
 * charging prices span 48% where pump prices span 13%. Each card is read against
 * its own fleet line, so what you see is the choice and not the drivetrain.
 */
export function TankGedragPanel({
  gedrag,
  periodLabel,
}: {
  gedrag: TankGedragOverzicht;
  periodLabel: string;
}) {
  if (gedrag.soorten.length === 0) {
    return (
      <Panel>
        <div style={{ fontSize: 13, color: '#888888' }}>
          Geen tank- of laadtransacties met een hoeveelheid in deze periode.
        </div>
      </Panel>
    );
  }

  // Onder elkaar op volle breedte, net als de panelen erboven. Naast elkaar
  // stonden ze op halve breedte, en juist de balk heeft de ruimte nodig: die
  // tekent afwijkingen van een paar procent, en op 160 px is dat een paar pixels.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {gedrag.soorten.map((s) => (
        <Kaart key={s.soort} soort={s} periodLabel={periodLabel} />
      ))}
    </div>
  );
}
